import { useEffect, useState } from "react";

const STORAGE_KEY = "themeMode";
const DEFAULT_MODE = "light";

function readMode() {
    try {
        const persisted = localStorage.getItem(STORAGE_KEY);
        return persisted === "dark" || persisted === "light"
            ? persisted
            : DEFAULT_MODE;
    } catch {
        return DEFAULT_MODE;
    }
}

function applyMode(mode) {
    const root = document.documentElement;
    root.classList.toggle("theme-dark", mode === "dark");
    root.classList.toggle("theme-light", mode !== "dark");
}

/**
 * Replaces MUI's `useTheme()` for this app.
 *
 * Styling is driven entirely by the `theme-light` / `theme-dark` class on <html>
 * (see Styles/tokens.css) — `style-src 'self'` blocks the runtime style injection
 * a JS theme object would need. This hook exists only for the handful of cases
 * that are not styling at all and genuinely need the value in JS, such as picking
 * a different logo *file* per theme. Prefer a CSS token over calling this.
 *
 * public/theme-preload.js sets the class before first paint; this keeps it in sync
 * afterwards and mirrors changes made in other tabs.
 */
export default function useThemeMode() {
    const [mode, setMode] = useState(readMode);

    // Normalise a missing/garbage persisted value so other tabs agree on the default.
    useEffect(() => {
        try {
            const persisted = localStorage.getItem(STORAGE_KEY);
            if (persisted !== "light" && persisted !== "dark") {
                localStorage.setItem(STORAGE_KEY, DEFAULT_MODE);
            }
        } catch {
            // storage unavailable — the in-memory default still applies
        }
    }, []);

    useEffect(() => {
        applyMode(mode);
    }, [mode]);

    useEffect(() => {
        const onStorage = (e) => {
            if (e?.key !== STORAGE_KEY) return;
            setMode(e.newValue === "dark" ? "dark" : "light");
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    return mode;
}
